import React, { useState } from 'react';
import { Save, Key, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const PassphraseManager = () => {
  const { toast } = useToast();
  const [currentPassphrase, setCurrentPassphrase] = useState('');
  const [newPassphrase, setNewPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Get current stored passphrase (for display info only)
  const storedPassphrase = localStorage.getItem('admin_passphrase') || 'EDGETRADE_SECURE_2024';
  const isDefaultPassphrase = storedPassphrase === 'EDGETRADE_SECURE_2024';

  const handleChangePassphrase = () => {
    // Validation
    if (!currentPassphrase || !newPassphrase || !confirmPassphrase) {
      toast({
        title: 'Error',
        description: 'Please fill all fields',
        variant: 'destructive'
      });
      return;
    }

    // Verify current passphrase
    if (currentPassphrase !== storedPassphrase) {
      toast({
        title: 'Error',
        description: 'Current passphrase is incorrect',
        variant: 'destructive'
      });
      return;
    }

    // Check new passphrase match
    if (newPassphrase !== confirmPassphrase) {
      toast({
        title: 'Error',
        description: 'New passphrases do not match',
        variant: 'destructive'
      });
      return;
    }

    // Check passphrase strength
    if (newPassphrase.length < 8) {
      toast({
        title: 'Error',
        description: 'Passphrase must be at least 8 characters',
        variant: 'destructive'
      });
      return;
    }

    // Save new passphrase
    localStorage.setItem('admin_passphrase', newPassphrase);

    toast({
      title: 'Success',
      description: 'Admin passphrase updated successfully'
    });

    // Clear fields
    setCurrentPassphrase('');
    setNewPassphrase('');
    setConfirmPassphrase('');
  };

  const handleResetToDefault = () => {
    if (window.confirm('Are you sure you want to reset to the default passphrase?')) {
      localStorage.removeItem('admin_passphrase');
      toast({
        title: 'Success',
        description: 'Passphrase reset to default: EDGETRADE_SECURE_2024'
      });
      setCurrentPassphrase('');
      setNewPassphrase('');
      setConfirmPassphrase('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Passphrase Manager</h1>
        <p className="text-cyan-400">Manage admin security checkpoint passphrase</p>
      </div>

      {/* Current Status */}
      <div className={`border rounded-xl p-6 ${
        isDefaultPassphrase 
          ? 'bg-orange-900/20 border-orange-500/30' 
          : 'bg-green-900/20 border-green-500/30'
      }`}>
        <div className="flex items-start gap-3">
          <AlertTriangle className={`w-6 h-6 flex-shrink-0 mt-0.5 ${
            isDefaultPassphrase ? 'text-orange-400' : 'text-green-400'
          }`} />
          <div>
            <h3 className={`font-semibold mb-1 ${
              isDefaultPassphrase ? 'text-orange-400' : 'text-green-400'
            }`}>
              {isDefaultPassphrase ? 'Default Passphrase Active' : 'Custom Passphrase Active'}
            </h3>
            <p className={`text-sm ${
              isDefaultPassphrase ? 'text-orange-300' : 'text-green-300'
            }`}>
              {isDefaultPassphrase 
                ? 'You are using the default passphrase. Consider changing it for better security.' 
                : 'You have set a custom passphrase. Make sure to remember it!'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Key className="w-5 h-5 text-cyan-400" />
          Current Passphrase Information
        </h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
            <span className="text-cyan-400 font-semibold">Status:</span>
            <span className={`font-bold ${
              isDefaultPassphrase ? 'text-orange-400' : 'text-green-400'
            }`}>
              {isDefaultPassphrase ? 'Default' : 'Custom'}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
            <span className="text-cyan-400 font-semibold">Passphrase:</span>
            <span className="text-white font-mono">
              {showCurrent ? storedPassphrase : '••••••••••••••'}
              <button
                onClick={() => setShowCurrent(!showCurrent)}
                className="ml-2 text-cyan-400 hover:text-cyan-300 text-sm"
              >
                {showCurrent ? 'Hide' : 'Show'}
              </button>
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
            <span className="text-cyan-400 font-semibold">Length:</span>
            <span className="text-white font-semibold">{storedPassphrase.length} characters</span>
          </div>
        </div>
      </div>

      {/* Change Passphrase Form */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-cyan-400" />
          Change Admin Passphrase
        </h2>
        
        <div className="space-y-4">
          {/* Current Passphrase */}
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Current Passphrase
            </label>
            <input
              type="password"
              value={currentPassphrase}
              onChange={(e) => setCurrentPassphrase(e.target.value)}
              placeholder="Enter current passphrase"
              className="w-full px-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-800 focus:outline-none focus:border-cyan-400"
            />
          </div>

          {/* New Passphrase */}
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              New Passphrase
            </label>
            <input
              type={showNew ? 'text' : 'password'}
              value={newPassphrase}
              onChange={(e) => setNewPassphrase(e.target.value)}
              placeholder="Enter new passphrase (min 8 characters)"
              className="w-full px-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-800 focus:outline-none focus:border-cyan-400"
            />
            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={showNew}
                onChange={(e) => setShowNew(e.target.checked)}
                className="w-4 h-4"
              />
              <label className="text-cyan-600 text-sm">Show passphrase</label>
            </div>
          </div>

          {/* Confirm Passphrase */}
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Confirm New Passphrase
            </label>
            <input
              type="password"
              value={confirmPassphrase}
              onChange={(e) => setConfirmPassphrase(e.target.value)}
              placeholder="Re-enter new passphrase"
              className="w-full px-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-800 focus:outline-none focus:border-cyan-400"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleChangePassphrase}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white font-bold transition-all"
            >
              <Save className="w-5 h-5" />
              Update Passphrase
            </button>
            {!isDefaultPassphrase && (
              <button
                onClick={handleResetToDefault}
                className="px-6 py-3 rounded-lg bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border border-orange-500/30 font-semibold transition-all"
              >
                Reset to Default
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Security Tips */}
      <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-blue-400" />
          Passphrase Security Tips
        </h3>
        <ul className="space-y-2 text-sm text-blue-300">
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span>Use at least 12 characters for maximum security</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span>Mix uppercase, lowercase, numbers, and special characters</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span>Avoid using common words or personal information</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span>Change your passphrase periodically for better security</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span><strong>Important:</strong> Store your passphrase securely - there is no recovery option!</span>
          </li>
        </ul>
      </div>

      {/* Access Path Info */}
      <div className="bg-slate-800/50 border border-cyan-500/20 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-3">Admin Access Path</h3>
        <div className="text-sm text-cyan-400 space-y-2">
          <p><strong>Step 1:</strong> Go to Settings → Security</p>
          <p><strong>Step 2:</strong> Triple-click "Advanced Security Center"</p>
          <p><strong>Step 3:</strong> Enter passphrase when prompted</p>
          <p><strong>Step 4:</strong> Enter admin credentials</p>
          <p className="text-cyan-600 text-xs mt-4">Alternative: Triple-click the shield icon in Settings header</p>
        </div>
      </div>
    </div>
  );
};

export default PassphraseManager;
